import React, {useCallback, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getLanguageCode} from 'modules/meta/selectors'
import {useTranslateReviewsQuery} from 'modules/translateReviewsApi'
import {equals} from 'ramda'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {useGetUserReviewsQuery} from '@vio/core/src/sapi/services/searchApi'

import {GuestReviews} from '../GuestReviews'
import {ReviewListWrapper} from '../ReviewListWrapper'
import {ReviewsLoadingList} from '../ReviewLoading'
import {reviewMessages} from '../ReviewMessages'
import {ReviewSearchInput} from '../ReviewSearchInput'
import {ReviewSorting} from '../ReviewSorting'

const ReviewCommentsWrapper = styled.div`
  &.--accommodation-reviews-section {
    grid-area: comments;
    ${linariaMq.desktopMd} {
      min-width: 560px;
    }
    &.--is-overlay {
      ${linariaMq.desktopXs} {
        width: 608px;
      }

      ${linariaMq.desktopSm} {
        width: 864px;
      }

      ${linariaMq.desktopMd} {
        width: 100%;
        min-width: 488px;
      }
    }
  }
`

interface Props {
  hotelId?: string
  isOverlay?: boolean
  isHotelDetailsTab?: boolean
}

export const REVIEWS_PAGE_SIZE = 10
export const ReviewsList: React.FC<Props> = ({
  hotelId,
  isOverlay = false,
  isHotelDetailsTab = false
}) => {
  const toggle = useToggle()
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)

  const [offset, setOffset] = useState(0)
  const [sortBy, setSortBy] = useState(null)
  const [searchReviews, setSearchReviews] = useState<string[]>([])

  const {
    currentData: data, // currentData is reset when sort/search changes
    isLoading: isReviewsRequestLoading,
    isFetching: isReviewRequestFetching
  } = useGetUserReviewsQuery(
    {
      hotelId,
      language: languageCode,
      pageSize: REVIEWS_PAGE_SIZE,
      offset,
      sortBy,
      ...(searchReviews.length > 0 && {search: searchReviews})
    },
    {
      skip: !hotelId
    }
  )

  // Capped to Hebrew initially
  const {data: translatedReviews, isLoading: isTranslatingReviews} =
    useTranslateReviewsQuery(
      {hotelId, lang: languageCode},
      {
        skip: toggle(
          'feature-translate-reviews',
          true,
          !hotelId || !languageCode
        )
      }
    )

  const reviews = data?.reviews ?? []
  const showLoadingState =
    !reviews.length && (isReviewRequestFetching || isReviewsRequestLoading)
  const showNoResults = !reviews.length && searchReviews.length > 0
  const noMoreReviews = Boolean(data?.nextOffset < 1)
  const canShowBackToTop = reviews.length > REVIEWS_PAGE_SIZE

  const handleSearchReviews = (searches: string[]) => {
    if (searches.length > 0) {
      setSearchReviews(prev => {
        if (!equals(prev, searches)) {
          setOffset(0)
          return searches
        }

        return prev
      })
    } else {
      setSearchReviews(prev => {
        if (prev.length > 0) {
          setOffset(0)
        }

        return []
      })
    }
  }

  const handleReviewSortChange = useCallback(
    (value: string) => {
      if (value !== sortBy) {
        setOffset(0)
        setSortBy(value)
      }
    },
    [sortBy]
  )

  if (!reviews.length && searchReviews.length === 0 && !sortBy) {
    if (showLoadingState)
      return (
        <div className="--with-side-padding">
          {isMobile && (
            <Box paddingBottom="s700">
              <Divider />
            </Box>
          )}
          <ReviewsLoadingList />
        </div>
      )

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap="s300"
        paddingTop="s950"
        paddingBottom="s950"
      >
        <Text variant="titleM" align="center">
          {formatMessage(reviewMessages.noReviewsTitle)}
        </Text>
        <Text variant="bodyS" align="center">
          {formatMessage(reviewMessages.noReviewsDescription)}
        </Text>
      </Box>
    )
  }

  if (isOverlay) {
    return (
      <ReviewCommentsWrapper
        className={cx(
          !isHotelDetailsTab && '--accommodation-reviews-section',
          '--with-side-padding',
          isOverlay && '--is-overlay'
        )}
      >
        <ReviewListWrapper canShowBackToTop={canShowBackToTop}>
          {isMobile && (
            <Box paddingBottom="s700">
              <Divider />
            </Box>
          )}
          <Box
            paddingBottom="s500"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text variant="titleL">
              {formatMessage(reviewMessages.guestReviewsTitle)}
            </Text>
            <ReviewSorting onChange={handleReviewSortChange} />
          </Box>
          <ReviewSearchInput onSearch={handleSearchReviews} />
          {showLoadingState ? (
            <ReviewsLoadingList />
          ) : showNoResults ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="s300"
              paddingTop="s700"
              paddingBottom="s950"
            >
              <Text variant="titleM" align="center">
                {formatMessage(reviewMessages.reviewSectionNoResultsTitle)}
              </Text>
              <Text variant="bodyS" align="center">
                {formatMessage(
                  reviewMessages.reviewSectionNoResultsDescription
                )}
              </Text>
            </Box>
          ) : (
            <GuestReviews
              key="reviewComments"
              mountedOnOverlay
              reviewsData={reviews}
              noMoreReviews={noMoreReviews}
              onLoadMore={() => setOffset(data?.nextOffset)}
              sourceComponent={
                isHotelDetailsTab
                  ? 'HotelDetailsTab'
                  : 'AccommodationReviewsSection'
              }
              searchKeywords={searchReviews}
              reviewsAvailable={data.reviewsAvailable}
              isTranslatingReviews={isTranslatingReviews}
              translatedReviews={translatedReviews}
            />
          )}
        </ReviewListWrapper>
      </ReviewCommentsWrapper>
    )
  }

  if (reviews.length > 0) {
    return (
      <ReviewCommentsWrapper className="--accommodation-reviews-section">
        <GuestReviews
          key="reviewComments"
          reviewsData={reviews}
          noMoreReviews={noMoreReviews}
          onLoadMore={() => setOffset(data?.nextOffset)}
          sourceComponent="AccommodationReviewsSection"
          isTranslatingReviews={isTranslatingReviews}
          translatedReviews={translatedReviews}
        />
      </ReviewCommentsWrapper>
    )
  }

  return null
}
